import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { useNavigate } from "@/lib/router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAuth } from "reactfire";
import { CHECKOUT_EVENTS } from "../../events.checkout";
import { handleReactivatePlan } from "../../handlers/handleReactivatePlan";
import { CheckoutService } from "../../service.checkout";

interface UseReactivatePlanMutationOptions {
  isV1?: boolean;
  onSuccessCallback?: () => void;
}

export function useReactivatePlanMutation(
  options?: UseReactivatePlanMutationOptions,
) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("No authentication token");

      const result = await handleReactivatePlan({
        token,
        isV1: options?.isV1,
      });

      if (!result.success) {
        throw new Error("Failed to reactivate plan");
      }

      return result;
    },
    onSuccess: () => {
      // Refetch user plan data to update the UI
      queryClient.invalidateQueries({ queryKey: [USER_PLAN_QUERY_KEY] });
      CheckoutService.track(CHECKOUT_EVENTS.REACTIVATION_SUCCESS, {
        types: ["posthog", "customerio"],
      });

      if (options?.onSuccessCallback) {
        options.onSuccessCallback();
      } else {
        toast.success("Plan reactivated successfully");
        navigate("/");
      }
    },
    onError: (error) => {
      CheckoutService.track(CHECKOUT_EVENTS.REACTIVATION_FAILED, {
        types: ["posthog", "customerio"],
        properties: {
          error: error instanceof Error ? error.message : "Unknown error",
        },
      });
      console.error("Error reactivating plan:", error);
      toast.error("Failed to reactivate plan. Please try again.");
    },
  });
}
